/** Additive v1 economy data: older saves keep their unknown fields and gain defaults on boot. */
import type { SliceDef } from '../shared';
import { readEquipment, validateEquipment, type EquipmentSave } from './equipment';

export type TrainingStat = 'might' | 'grit' | 'cunning';
export type ChapterActivity = 'arena' | 'road' | 'cards';
export interface ChapterTrack { v: 1; chapter: number; consumed: Record<ChapterActivity, number> }
export interface EconomySave {
  v: 1;
  gold: number;
  xp: number;
  level: number;
  training: Record<TrainingStat, number>;
  activities: { arena: number; road: number; cards: number };
  chapters: number;
  chapterTrack?: ChapterTrack;
  equipment: EquipmentSave;
  currencies: Record<string, number>;
  claims: string[];
  ledger: Array<{ id: string; source: string; xp: number; gold: number }>;
  [k: string]: unknown;
}
const num = (v: unknown, fallback = 0): number => typeof v === 'number' && Number.isFinite(v) && v >= 0 ? Math.floor(v) : fallback;
function validTrack(value: unknown, chapter: number): value is ChapterTrack {
  const t = value as Partial<ChapterTrack> | undefined;
  return !!t && t.v === 1 && t.chapter === chapter && ['arena', 'road', 'cards'].every(key => {
    const n = t.consumed?.[key as ChapterActivity]; return typeof n === 'number' && Number.isSafeInteger(n) && n >= 0;
  });
}
export function readEconomy(value: unknown): EconomySave {
  const s = (typeof value === 'object' && value !== null ? value : {}) as Partial<EconomySave>;
  return { ...s, v: 1, gold: num(s.gold, 35), xp: num(s.xp), level: Math.max(1, num(s.level, 1)),
    training: { might: num(s.training?.might), grit: num(s.training?.grit), cunning: num(s.training?.cunning) },
    activities: { arena: num(s.activities?.arena), road: num(s.activities?.road), cards: num(s.activities?.cards) },
    chapters: num(s.chapters), chapterTrack: validTrack(s.chapterTrack, num(s.chapters)) ? structuredClone(s.chapterTrack) : undefined,
    equipment: readEquipment(s.equipment), currencies: Object.fromEntries(Object.entries(s.currencies ?? {}).map(([k, v]) => [k, num(v)])),
    claims: Array.isArray(s.claims) ? s.claims.filter((v) => typeof v === 'string').slice(-200) : [],
    ledger: Array.isArray(s.ledger) ? s.ledger.filter((v) => v && typeof v.id === 'string' && typeof v.source === 'string' && Number.isFinite(v.xp) && Number.isFinite(v.gold)).slice(-20) : [],
  };
}
export const economySlice: SliceDef<EconomySave> = {
  key: 'economy', version: 1, create: () => readEconomy({}), migrations: {},
  validate: (x) => {
    if (typeof x !== 'object' || x === null || typeof (x as { v?: unknown }).v !== 'number') return ['economy: expected an object with a numeric v'];
    const s = x as Partial<EconomySave>, errors = validateEquipment(s.equipment);
    if (s.chapterTrack !== undefined && !validTrack(s.chapterTrack, num(s.chapters))) errors.push('economy: invalid chapter progress');
    return errors;
  },
};
